import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, ScrollText, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { BrandLockup, BrandShell } from "./AuthShell";

const STEPS = [
  { title: "reset.step1Title", sub: "reset.step1Sub" },
  { title: "reset.step2Title", sub: "reset.step2Sub" },
  { title: "reset.step3Title", sub: "reset.step3Sub" },
];

// Per-accent classes for the progress rail. Tenant setup is red; the platform /
// super-admin setup axis is purple (6.6).
const ACCENTS = {
  red: {
    glow: "red" as const,
    logo: "var(--color-red)",
    eyebrow: "text-red-ink",
    active: "border-red text-red",
    done: "border-red bg-red text-on-amber",
    line: "bg-red",
  },
  platform: {
    glow: "platform" as const,
    logo: "var(--color-platform)",
    eyebrow: "text-platform-ink",
    active: "border-platform text-platform",
    done: "border-platform bg-platform text-on-platform",
    line: "bg-platform",
  },
};

// SetupBrand is the first-time-setup / onboarding panel with a 3-step progress
// rail: Set password → Enroll 2FA → Recovery codes. `activeStep` (1-based) paints
// the current step outlined in the accent, earlier steps done (filled + check),
// later steps pending; pass a step past the last to mark all done. `accent` picks
// red (tenant) or purple (platform). The eyebrow/headline/tagline/footer and the
// lockup vary per screen, so one panel serves every setup screen on both axes.
export function SetupBrand({
  activeStep = 1,
  eyebrow = "reset.eyebrow",
  headline = "reset.brandHeadline",
  para = "reset.brandPara",
  footer = "reset.brandFooter",
  footerIcon: FooterIcon = ScrollText,
  accent = "red",
  lockup,
  showSteps = true,
}: {
  activeStep?: number;
  eyebrow?: string;
  headline?: string;
  para?: string;
  footer?: string;
  footerIcon?: LucideIcon;
  accent?: "red" | "platform";
  lockup?: ReactNode;
  // Success screens reuse this panel for its accent + copy but hide the rail.
  showSteps?: boolean;
}) {
  const { t } = useTranslation();
  const a = ACCENTS[accent];
  return (
    <BrandShell glow={a.glow}>
      {lockup ?? <BrandLockup accent={a.logo} />}

      <div className="flex max-w-xl flex-col gap-5">
        <span className={cn("font-mono text-xs tracking-[0.14em]", a.eyebrow)}>{t(eyebrow)}</span>
        <h1 className="max-w-130 font-head text-[44px] font-bold leading-[1.1] tracking-tight text-ink">
          {t(headline)}
        </h1>
        <p className="max-w-117.5 text-[16px] leading-relaxed text-muted">{t(para)}</p>

        {showSteps && (
        <ol className="flex flex-col pt-2">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const active = n === activeStep;
            const done = n < activeStep;
            const last = n === STEPS.length;
            return (
              <li key={s.title} className="flex gap-3.5">
                {/* Rail: numbered node + connecting line. */}
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      "grid size-7 shrink-0 place-items-center rounded-full border-[1.5px] font-head text-sm font-bold",
                      active ? a.active : done ? a.done : "border-edge-3 text-muted",
                    )}
                  >
                    {done ? <Check size={14} strokeWidth={3} /> : n}
                  </span>
                  {!last && (
                    <span className={cn("h-10 w-0.5 rounded-full", done ? a.line : "bg-edge-2")} />
                  )}
                </div>
                {/* Content: title + subtitle. */}
                <div className="flex flex-col gap-0.5 pt-0.5">
                  <span
                    className={cn(
                      "text-[15px] font-semibold",
                      active || done ? "text-ink" : "text-muted",
                    )}
                  >
                    {t(s.title)}
                  </span>
                  <span className={cn("text-[13px]", active || done ? "text-muted" : "text-muted-2")}>
                    {t(s.sub)}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
        )}
      </div>

      <div className="flex items-center gap-2.5 text-[13px] text-muted-2">
        <FooterIcon size={16} />
        {t(footer)}
      </div>
    </BrandShell>
  );
}
